import { ArbeidsstedResponse, ArbeidstakerResponse } from '@/api/aaregDistApi'
import type { Texts } from '@/texts/getTexts'
import { AxiosError } from 'axios'
// Type-only: SortedTable importerer denne modulen, og en verdi-import ville gitt en sirkulær avhengighet.
import type { SortedTableProps } from 'components/sorted-table/SortedTable'
import { createReducer } from 'redunk'
import { combineReducers, createStore } from 'redux'
import { AltinnOrganisasjon, Bruker } from 'types/api'
import { ArbeidsforholdDetaljert } from 'types/arbeidsforhold'
import { Dataelementer } from 'types/dataminimering'
import { ArbeidsforholdstatusEnum, ArbeidsforholdtypeEnum, RapporteringsordningEnum, Soeketype } from 'types/oppslag'

export interface Store {
    showLogoutWarning: boolean
    godtattVilkaar: boolean
    bruker: {
        loading: boolean
        error?: AxiosError['response']
        valgtOrg?: AltinnOrganisasjon
        data?: Bruker & { valgtOrg?: AltinnOrganisasjon }
    }
    arbeidsgiver: {
        view: 'hovedenhet' | 'arbeidssted' | 'arbeidsforhold'
        arbeidssted: ArbeidsstedResponse | null
        arbeidsstedLoading: boolean
        arbeidsforhold: ArbeidsforholdDetaljert | null
        arbeidsforholdLoading: boolean
    }
    arbeidstaker: {
        view: 'arbeidstaker' | 'arbeidsforhold'
        arbeidsforhold: ArbeidsforholdDetaljert | null
        arbeidsforholdLoading: boolean
    }
    samarbeidspartner: {
        view: 'soek' | 'arbeidsforhold'
        arbeidsforhold: ArbeidsforholdDetaljert | null
        arbeidsforholdLoading: boolean
    }
    datavalgtVistVedFoersteSoek: boolean
    lagreDataValg: boolean
    trefflisteData: ArbeidsstedResponse | ArbeidstakerResponse | null
    trefflisteLoading: boolean
    trefflisteFilter: 'alle' | 'aktive' | 'avsluttede' | 'fremtidige'
    dataminimeringValg: Dataelementer[]
    activeTab: 'soek' | 'treffliste' | 'datavalg' | 'arbeidsforhold' // Hack to get intellisense while extending any string
    activeArbeidsforhold?: string
    arbeidsforhold: Record<
        ArbeidsforholdDetaljert['id'],
        {
            loading: boolean
            error?: AxiosError
            data?: ArbeidsforholdDetaljert
            needsRefresh?: boolean
            views: {
                historikk: {
                    hiddenColumns: Record<string, boolean>
                    expanded?: boolean
                    page: number
                    pageSize: number
                }
                utland: {
                    expanded?: boolean
                    page?: number
                    pageSize?: number
                }
                permisjoner: {
                    expanded?: boolean
                    page?: number
                    pageSize?: number
                }
                permitteringer: {
                    expanded?: boolean
                    page?: number
                    pageSize?: number
                }
                timer: {
                    expanded?: boolean
                    page?: number
                    pageSize?: number
                }
            }
        }
    >
    begrepsbeskrivelser: Record<string, { loading?: boolean; error?: string; definisjon?: string; forklaring?: string; kilde?: string }>
    formState: {
        type: Soeketype.ARBEIDSSTED | Soeketype.ARBEIDSTAKER
        ident: string
        rapporteringsordning: (keyof typeof RapporteringsordningEnum)[]
        arbeidsforholdstatus: (keyof typeof ArbeidsforholdstatusEnum)[]
        arbeidsforholdtype: (keyof typeof ArbeidsforholdtypeEnum)[]
    }
    treffliste: Partial<SortedTableProps<any>>
    tekst: Texts
}

const initialState = {
    formState: {
        type: Soeketype.ARBEIDSSTED,
        ident: '',
        rapporteringsordning: [],
        arbeidsforholdstatus: [],
        arbeidsforholdtype: [],
    },
    trefflisteFilter: 'aktive',
    showLogoutWarning: false,
    bruker: { loading: true },
    godtattVilkaar: false,
    datavalgtVistVedFoersteSoek: false,
    dataminimeringValg: [], //Legge inn YRke som standardvalg etter https://jira.adeo.no/browse/TAF-784
    lagreDataValg: false,
    trefflisteData: null,
    trefflisteLoading: false,
    soekResponse: undefined,
    activeTab: 'soek',
    begrepsbeskrivelser: {},
    arbeidsforhold: {},
    arbeidsgiver: { view: 'hovedenhet', arbeidssted: null, arbeidsstedLoading: false, arbeidsforhold: null, arbeidsforholdLoading: false },
    arbeidstaker: { view: 'arbeidstaker', arbeidsforhold: null, arbeidsforholdLoading: false },
    samarbeidspartner: { view: 'soek', arbeidsforhold: null, arbeidsforholdLoading: false },
    treffliste: { page: 1, rowsPerPage: 50 },
    tekst: undefined as unknown as Texts,
} as Store

export const store = createReducer({
    name: 'soekReducer',
    initialState,
    reducers: (state) => ({
        SET_TREFFLISTE_DATA: (data: Store['trefflisteData']) => {
            state.trefflisteData = data
            state.trefflisteLoading = false
        },
        REQUEST_TREFFLISTE: () => {
            state.trefflisteLoading = true
        },
        SET_VALGT_ORG: (org: AltinnOrganisasjon) => {
            state.bruker.valgtOrg = org
            if (state.bruker.data) state.bruker.data.valgtOrg = org
            state.arbeidsgiver.view = 'hovedenhet'
        },
        SET_ARBEIDSGIVER_VIEW: (view: Store['arbeidsgiver']['view']) => {
            state.arbeidsgiver.view = view
        },
        REQUEST_ARBEIDSGIVER_ARBEIDSSTED: () => {
            state.arbeidsgiver.arbeidsstedLoading = true
        },
        SET_ARBEIDSGIVER_ARBEIDSSTED: (data: ArbeidsstedResponse | null) => {
            state.arbeidsgiver.arbeidssted = data
            state.arbeidsgiver.arbeidsstedLoading = false
        },
        REQUEST_ARBEIDSGIVER_ARBEIDSFORHOLD: () => {
            state.arbeidsgiver.arbeidsforholdLoading = true
        },
        SET_ARBEIDSGIVER_ARBEIDSFORHOLD: (data: ArbeidsforholdDetaljert | null) => {
            state.arbeidsgiver.arbeidsforhold = data
            state.arbeidsgiver.arbeidsforholdLoading = false
        },
        SET_ARBEIDSTAKER_VIEW: (view: Store['arbeidstaker']['view']) => {
            state.arbeidstaker.view = view
        },
        REQUEST_ARBEIDSTAKER_ARBEIDSFORHOLD: () => {
            state.arbeidstaker.arbeidsforholdLoading = true
        },
        SET_ARBEIDSTAKER_ARBEIDSFORHOLD: (data: ArbeidsforholdDetaljert | null) => {
            state.arbeidstaker.arbeidsforhold = data
            state.arbeidstaker.arbeidsforholdLoading = false
        },
        SET_SAMARBEIDSPARTNER_VIEW: (view: Store['samarbeidspartner']['view']) => {
            state.samarbeidspartner.view = view
        },
        REQUEST_SAMARBEIDSPARTNER_ARBEIDSFORHOLD: () => {
            state.samarbeidspartner.arbeidsforholdLoading = true
        },
        SET_SAMARBEIDSPARTNER_ARBEIDSFORHOLD: (data: ArbeidsforholdDetaljert | null) => {
            state.samarbeidspartner.arbeidsforhold = data
            state.samarbeidspartner.arbeidsforholdLoading = false
        },
        //Bruker
        BRUKER_REQUEST: () => {
            state.bruker.loading = true
            state.bruker.error = undefined
        },
        BRUKER_SUCCESS: (bruker: Bruker) => {
            state.bruker.data = bruker
            state.bruker.loading = false
        },
        SET_BRUKER: (bruker: Bruker, valgtOrg?: AltinnOrganisasjon) => {
            state.bruker.data = { ...bruker, valgtOrg }
            state.bruker.valgtOrg = valgtOrg
            state.bruker.loading = false
            state.bruker.error = undefined
        },
        BRUKER_FAILURE: (error: Store['bruker']['error']) => {
            state.bruker.loading = false
            state.bruker.error = error
        },
        SET_ACTIVE_TAB: (tab: Store['activeTab']) => {
            state.activeTab = tab
        },
        SET_ACTIVE_ARBEIDSFORHOLD: (arbeidsforhold: Store['activeArbeidsforhold']) => {
            state.activeArbeidsforhold = arbeidsforhold
        },
        SET_SOEKEREQUEST: (props: Store['formState']) => {
            state.formState = { ...props }
            Object.entries(props).forEach(([key, value]) => {
                window.sessionStorage.setItem(key, JSON.stringify(value))
            })
        },
        HYDRATE_FROM_SESSION: () => {
            if (typeof window === 'undefined') return
            const read = <T>(key: string, fallback: T): T => JSON.parse(window.sessionStorage.getItem(key) || JSON.stringify(fallback))
            state.formState.rapporteringsordning = read('rapporteringsordning', state.formState.rapporteringsordning)
            state.formState.arbeidsforholdstatus = read('arbeidsforholdstatus', state.formState.arbeidsforholdstatus)
            state.formState.arbeidsforholdtype = read('arbeidsforholdtype', state.formState.arbeidsforholdtype)
            state.dataminimeringValg = read('dataelementer', state.dataminimeringValg)
            state.lagreDataValg = read('lagreDataValg', state.lagreDataValg)
        },
        SET_STATE: (newState: Partial<Store>) => {
            Object.keys(newState).forEach((item) => {
                //@ts-ignore
                state[item] = newState[item]
            })
        },
        SET_LAGRE_DATA_VALG: (value: boolean) => {
            state.lagreDataValg = value
            if (typeof window !== 'undefined') window.sessionStorage.setItem('lagreDataValg', JSON.stringify(value))
        },
        SET_DATAMINIMERING_VALG: (valg: Store['dataminimeringValg']) => {
            state.dataminimeringValg = valg
            state.datavalgtVistVedFoersteSoek = true
            if (typeof window !== 'undefined') window.sessionStorage.setItem('dataelementer', JSON.stringify(valg))
            Object.keys(state.arbeidsforhold).forEach((arbeidsforholdId) => (state.arbeidsforhold[arbeidsforholdId].needsRefresh = true))
        },
        SET_ARBEIDSFORHOLD: (id: ArbeidsforholdDetaljert['id'], arbeidsforhold: ArbeidsforholdDetaljert) => {
            state.arbeidsforhold[id].data = arbeidsforhold
            state.arbeidsforhold[id].loading = false
        },
        REQUEST_ARBEIDSFORHOLD: (id: ArbeidsforholdDetaljert['id']) => {
            state.arbeidsforhold[id] = {
                loading: true,
                needsRefresh: false,
                views: {
                    historikk: { page: 0, pageSize: 10, hiddenColumns: {} },
                    permisjoner: {},
                    permitteringer: {},
                    timer: {},
                    utland: {},
                },
            }
        },
        ERROR_ARBEIDSFORHOLD: (id: ArbeidsforholdDetaljert['id'], error: Store['arbeidsforhold'][0]['error']) => {
            state.arbeidsforhold[id].error = error
            state.arbeidsforhold[id].loading = false
        },
        SET_ARBEIDSFORHOLD_VIEW: <T extends ArbeidsforholdDetaljert['id'], S extends keyof Partial<Store['arbeidsforhold'][T]['views']>>(
            id: T,
            view: S,
            data: Partial<Store['arbeidsforhold'][T]['views'][S]>,
        ) => {
            Object.keys(data).forEach((item) => {
                //@ts-ignore
                state.arbeidsforhold[id].views[view][item] = data[item]
            })
        },
        CLOSE_ARBEIDSFORHOLD: (id: ArbeidsforholdDetaljert['id']) => {
            delete state.arbeidsforhold[id]
        },
        CLOSE_ALL_ARBEIDSFORHOLD: () => {
            state.arbeidsforhold = {}
        },
        RESET_TREFFLISTE: () => {
            state.treffliste = { page: 1, rowsPerPage: 50 }
        },
        //begrepsbeskrivelser
        REQUEST_BEGREP: (begrepId: keyof Store['begrepsbeskrivelser']) => {
            state.begrepsbeskrivelser[begrepId] = { loading: true, error: undefined }
        },
        SUCCESS_BEGREP: (begrepId: keyof Store['begrepsbeskrivelser'], begrep: Store['begrepsbeskrivelser'][0]) => {
            state.begrepsbeskrivelser[begrepId] = { loading: false, error: undefined, ...begrep }
        },
        FAILURE_BEGREP: (begrepId: keyof Store['begrepsbeskrivelser'], error) => {
            state.begrepsbeskrivelser[begrepId].loading = false
            state.begrepsbeskrivelser[begrepId].error = error
        },

        //Logout warning
        SET_LOGOUT_WARNING: (value: boolean) => {
            state.showLogoutWarning = value
        },

        //Treffliste
        SET_TREFFLISTE_STATE: (trefflisteState: Store['treffliste']) => {
            state.treffliste = trefflisteState
        },
    }),
})

export const reduxStore = createStore(
    combineReducers({ ...store.namedReducer }),
    typeof window !== 'undefined'
        ? //@ts-expect-error
          window.__REDUX_DEVTOOLS_EXTENSION__ &&
              //@ts-expect-error
              window.__REDUX_DEVTOOLS_EXTENSION__()
        : undefined,
)

export const instantiateStore = (initialData?: Partial<Store>) => {
    return createStore(
        combineReducers({ ...store.namedReducer }),
        { soekReducer: { ...initialState, ...initialData } as Store },
        typeof window !== 'undefined'
            ? //@ts-expect-error
              window.__REDUX_DEVTOOLS_EXTENSION__ &&
                  //@ts-expect-error
                  window.__REDUX_DEVTOOLS_EXTENSION__()
            : undefined,
    )
}
